import { ReactNode } from 'react';
import reactStringReplace from 'react-string-replace';
import dayjs from 'dayjs';

import {
  MessageData,
  MSG_EVENT,
  NotificationData,
  MSG_TYPE
} from '@DStypes/notifications';
import { Link } from '@mui/material';
import { FaIcon, MDBox, MDButton, MDTypography } from '@components';
import { sendAmplitudeData } from './amplitude';
import { CLICKED_KNOW_MORE } from '@constants/eventNames';
import store from '@store/store';
import { parseJSON } from './common';

interface IGenerateNotificationWithButton {
  message: string | React.ReactNode[];
  buttonText: string;
  buttonLink: string;
  navigate: any;
  customButtonStyles?: any;
}
const planUpgradeUrl = `${process.env.REACT_APP_PLAN_UPGRADE_URL}`;

export const getSnackbarMessage = (
  rawMessage: string,
  isNotificationPopup: boolean,
  navigate?: (to: any, state?: object | undefined) => void
) => {
  const customStyles = !isNotificationPopup
    ? { fontSize: '0.925rem', fontFamily: 'Open Sans', lineHeight: '1.6' }
    : {
        fontSize: '0.75rem'
      };

  if (
    rawMessage.includes(
      'You can read more about the different Cleaning Types here'
    )
  )
    return reactStringReplace(rawMessage, 'different Cleaning Types here', () =>
      renderButton(
        'different Cleaning Types here',
        'https://www.notion.so/datasquirrel/What-is-Data-Cleaning-e070801e111b4e9c9d3ae25e53e1708f?pvs=4',
        navigate,
        !isNotificationPopup
          ? customStyles
          : {
              fontFamily: 'inherit',
              color: 'inherit',
              fontWeight: 300,
              fontSize: '0.75rem'
            },
        true,
        undefined,
        undefined,
        'Clean Data'
      )
    ) as ReactNode[];

  const replaceKey =
    'You can \n- Download cleaned data file by clicking Download button \n- View cleaned rows and columns by clicking <table> icon';

  const htmlMsg = reactStringReplace(rawMessage, replaceKey, () => (
    <>
      <div>
        <MDTypography fontWeight='bold' {...customStyles}>
          You can
        </MDTypography>

        <div>
          <MDTypography fontWeight='bold' {...customStyles}>
            - Download cleaned data file by clicking Download button
          </MDTypography>
        </div>
        <div>
          <MDTypography fontWeight='bold' {...customStyles}>
            - View cleaned rows and columns by clicking{'  '}
            <FaIcon
              icon='table-layout'
              typographyProps={{ color: 'dark', variant: 'lg' }}
            />
            {'  '}
            icon
          </MDTypography>
        </div>
      </div>
    </>
  ));

  return htmlMsg;
};

export const generateLink = (messageObj: NotificationData) => {
  const messageData: MessageData = JSON.parse(messageObj.message_data);
  const userDetails = parseJSON(
    parseJSON(localStorage.getItem('user_details') || '') as string
  );

  switch (messageObj.message_event) {
    case MSG_EVENT.FILE_UPLOAD_SUCCESS:
      return `/project/${messageData.project.id}/review`;
    case MSG_EVENT.FILE_UPLOAD_FAIL:
      return 'https://www.notion.so/Want-to-know-more-about-Importing-data-into-DataSquirrel-ea284f2ef9f142939a2802ada6ff11b0';
    case MSG_EVENT.FILE_CLEAN:
      return `/project/${messageData.project.id}/edit`;
    case MSG_EVENT.GO_AUTO_SUCCESS:
      return `/project/${messageData.project.id}/edit`;
    case MSG_EVENT.GO_AUTO_ZERO_CHART:
      return `/project/${messageData.project.id}/edit`;
    case MSG_EVENT.ADD_CHART_SUCCESS:
      return `/chart/${messageData.chart_id}/edit`;
    case MSG_EVENT.EXCEL_MULTIPLE_TAB:
      return `/project/${messageData.project.id}/review`;
    case MSG_EVENT.FILE_PROCESSING:
      return `/`;
    case MSG_EVENT.CHART_THEME_UPDATED:
      return `/chart/${messageData.chart_id}/edit`;
    case MSG_EVENT.CHART_DOWNLOAD:
      return `/chart/${messageData.chart_id}/edit`;
    case MSG_EVENT.PROJECT_THEME_UPDATED:
      return `/project/${messageData.project.id}/edit`;
    case MSG_EVENT.DATA_ANONYMIZED:
      return `https://datasquirrel.notion.site/What-is-Sensitive-Data-Data-Anonymisation-17f688499d2045d99ec677c037f0c290`;
    case MSG_EVENT.REPORT_GENERATION_COMPLETED:
      return `/project/${messageData.project.id}/edit`;
    case MSG_EVENT.FEEDBACK_SUBMITTED:
      return 'https://docs.google.com/forms/d/e/1FAIpQLSeRUvBqbAFYOr6hAtQDfQqjc024yN44KdvSpF4S1ms1EaNL7w/viewform';
    case MSG_EVENT.API_INGESTION_LIMIT_EXCEEDED:
    case MSG_EVENT.API_RECALCULATION_LIMIT_EXCEEDED:
    case MSG_EVENT.FILE_ROW_COL_LIMITED:
      return `${planUpgradeUrl}?userid=${
        (userDetails as any)?.user?.id
      }&plan_level=PRO${
        messageData?.project?.id
          ? `&project_id=${messageData?.project?.id}`
          : ''
      }`;
    default:
      return '';
  }
};

const getMessage = (
  messageObj: NotificationData,
  isNotificationPopup: boolean,
  navigate?: (to: any, state?: object | undefined) => void
) => {
  const { message_text: text, message_data, message_event } = messageObj;
  const messageData: MessageData = JSON.parse(message_data);

  switch (message_event) {
    case MSG_EVENT.FILE_CLEAN:
      return getSnackbarMessage(text, isNotificationPopup, navigate);
    case MSG_EVENT.GO_AUTO_SUCCESS:
      return `${messageData?.chart_count || 0} ${
        messageData?.chart_count === 1 ? 'chart has' : 'charts have'
      } been successfully created using Go Auto`;
    case MSG_EVENT.REPORT_GENERATION_COMPLETED:
      return `Your report is ready! Click the button to download it on your computer’s drive. Ensure that your browser does not block download of files.\n<Download>`;
    case MSG_EVENT.CLEANED_FILE_GENERATION_COMPLETED:
      return `Your file is ready! Click the button to download it on your computer’s drive. Ensure that your browser does not block download of files.\n<Download>`;
    case MSG_EVENT.FILE_COMBINE_STARTED:
    case MSG_EVENT.FILE_COMBINE_SUCCESS:
      return reactStringReplace(text, '\n', () => <br />);
    default:
      return text;
  }
};

const renderButton = (
  buttonText: string | React.ReactNode[],
  buttonLink: string,
  navigate: any,
  customButtonStyles: any,
  needAnchorElement = false,
  needColon?: boolean,
  downloadReport?: any,
  feature_name?: string
) =>
  downloadReport ? (
    <MDBox display='flex' sx={{ justifyContent: 'flex-end' }}>
      <MDButton color='light' size='small' onClick={() => downloadReport()}>
        <MDTypography
          variant='xs'
          color='info'
          fontWeight='bold'
          textTransform='capitalise'
        >
          {buttonText}
        </MDTypography>
      </MDButton>
    </MDBox>
  ) : (
    <button
      style={{
        backgroundColor: 'transparent',
        border: 'none',
        fontWeight: '500',
        cursor: 'pointer',
        textUnderlineOffset: '3px',
        ...customButtonStyles
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!needAnchorElement) {
          e.preventDefault();
          if (downloadReport) downloadReport();
          else navigate(buttonLink);
        }
        if (feature_name)
          sendAmplitudeData(CLICKED_KNOW_MORE, {
            'Feature Name': feature_name
          });
      }}
    >
      {needAnchorElement ? (
        <span
          role='presentation'
          onClick={() => {
            if (buttonText === 'this section')
              sendAmplitudeData(CLICKED_KNOW_MORE);
          }}
        >
          <Link
            href={buttonLink}
            target='_blank'
            sx={{
              textDecoration: 'underline',
              textDecorationColor: customButtonStyles ? '#344e867a' : ''
            }}
          >
            <span>{buttonText}</span>
          </Link>
          {needColon && <span>: &nbsp;</span>}
        </span>
      ) : (
        <span>
          <span style={{ textDecoration: 'underline' }}>{buttonText}</span>
          {needColon && <span>: &nbsp;</span>}
        </span>
      )}
    </button>
  );

const generateNotificationWithButton = ({
  message,
  buttonText,
  buttonLink,
  navigate,
  customButtonStyles
}: IGenerateNotificationWithButton) => (
  <span>
    {renderButton(
      buttonText,
      buttonLink,
      navigate,
      customButtonStyles,
      false,
      true
    )}
    {message}
  </span>
);

export const getNotificationMessageWithLink = (
  notification: NotificationData,
  navigate: (to: any, state?: object | undefined) => void,
  isNotificationPopup: boolean,
  downloadReport?: (...args: any[]) => void
) => {
  const msgData: MessageData = JSON.parse(notification.message_data);

  const notificationMsg = getMessage(
    notification,
    isNotificationPopup,
    navigate
  );
  const notificationLink = generateLink(notification);

  let NotificationMsgWithLinks: ReactNode[];

  const customButtonStyles = !isNotificationPopup
    ? {
        fontSize: '0.925rem',
        fontFamily: 'Open Sans',
        lineHeight: '1.6'
      }
    : '';

  if (notification.message_event === MSG_EVENT.FILE_UPLOAD_FAIL) {
    NotificationMsgWithLinks = reactStringReplace(notificationMsg, 'here', () =>
      renderButton(
        notification.linkKeyword || 'here',
        notificationLink as string,
        navigate,
        customButtonStyles,
        true
      )
    ) as ReactNode[];

    NotificationMsgWithLinks = reactStringReplace(
      NotificationMsgWithLinks,
      '\n',
      () => <br />
    ) as ReactNode[];
  } else if (
    notification.message_event === MSG_EVENT.API_INGESTION_LIMIT_EXCEEDED ||
    notification.message_event === MSG_EVENT.API_RECALCULATION_LIMIT_EXCEEDED
  ) {
    NotificationMsgWithLinks = reactStringReplace(
      notificationMsg,
      'Upgrade',
      () =>
        renderButton(
          notification.linkKeyword || 'Upgrade',
          notificationLink as string,
          navigate,
          customButtonStyles,
          true
        )
    ) as ReactNode[];
  } else if (
    notification.message_event === MSG_EVENT.REPORT_GENERATION_COMPLETED
  ) {
    NotificationMsgWithLinks = reactStringReplace(
      notificationMsg,
      '<Download>',
      () =>
        renderButton(
          'Download',
          notificationLink as string,
          navigate,
          customButtonStyles,
          false,
          false,
          () =>
            downloadReport &&
            downloadReport(msgData.project.id, msgData?.url as string)
        )
    ) as ReactNode[];
    NotificationMsgWithLinks = reactStringReplace(
      NotificationMsgWithLinks,
      '\n',
      () => <br />
    ) as ReactNode[];
  } else if (notification.message_event === MSG_EVENT.FILE_CLEAN) {
    NotificationMsgWithLinks = reactStringReplace(notificationMsg, '\n', () => (
      <br />
    )) as ReactNode[];
  } else if (
    notification.message_event === MSG_EVENT.CLEANED_FILE_GENERATION_COMPLETED
  ) {
    NotificationMsgWithLinks = reactStringReplace(
      notificationMsg,
      '<Download>',
      () =>
        renderButton(
          'Download',
          notificationLink as string,
          navigate,
          customButtonStyles,
          false,
          false,
          () =>
            downloadReport &&
            downloadReport(msgData.project.id, msgData?.file_name as string)
        )
    ) as ReactNode[];
    NotificationMsgWithLinks = reactStringReplace(
      NotificationMsgWithLinks,
      '\n',
      () => <br />
    ) as ReactNode[];
  } else if (notification.message_event === MSG_EVENT.FILE_ROW_COL_LIMITED) {
    NotificationMsgWithLinks = reactStringReplace(
      notificationMsg,
      'Start your Free Pro Trial now',
      () =>
        renderButton(
          'Start your Free Pro Trial now',
          notificationLink as string,
          () => null,
          customButtonStyles,
          true,
          false
        )
    ) as ReactNode[];
    NotificationMsgWithLinks = reactStringReplace(
      NotificationMsgWithLinks,
      'purchase a plan',
      () =>
        renderButton(
          'purchase a plan',
          notificationLink as string,
          navigate,
          customButtonStyles,
          true,
          false
        )
    ) as ReactNode[];
  } else if (notification.message_event === MSG_EVENT.GO_AUTO_ZERO_CHART) {
    NotificationMsgWithLinks = reactStringReplace(
      notificationMsg,
      'Select Columns',
      () => <span style={{ fontWeight: 'bold' }}>Select Columns</span>
    ) as ReactNode[];
  } else if (
    notification.message_event === MSG_EVENT.FIELD_CATEGORISATION_SUCCESS
  ) {
    NotificationMsgWithLinks = reactStringReplace(
      notificationMsg,
      'You can now:',
      () => <span style={{ fontWeight: 'bold' }}>You can now:</span>
    ) as ReactNode[];
  } else {
    NotificationMsgWithLinks = reactStringReplace(
      notificationMsg,
      notification.replaceKeyword || 'click here',
      () =>
        renderButton(
          notification.linkKeyword || 'click here',
          notificationLink as string,
          navigate,
          customButtonStyles,
          notification.isAnchorElement,
          false,
          undefined,
          notification?.feature_name
        )
    ) as ReactNode[];

    NotificationMsgWithLinks = reactStringReplace(
      NotificationMsgWithLinks,
      'My Projects',
      () =>
        renderButton(
          'My Projects',
          '/project/my-projects',
          navigate,
          customButtonStyles
        )
    ) as ReactNode[];

    NotificationMsgWithLinks = reactStringReplace(
      NotificationMsgWithLinks,
      '\n',
      () => <br />
    ) as ReactNode[];
  }

  let message: string | JSX.Element = '';

  if (isNotificationPopup)
    message = <>{NotificationMsgWithLinks as JSX.Element[]}</>;
  else if (
    msgData?.chart_id &&
    !location.pathname.includes(msgData?.project?.id)
  )
    message = generateNotificationWithButton({
      message: NotificationMsgWithLinks,
      buttonText: msgData.project.name,
      buttonLink: notificationLink as string,
      navigate: navigate
    });
  else if (!msgData?.chart_id)
    if (
      msgData?.project?.id &&
      !location.pathname.includes(msgData?.project?.id) &&
      store.getState().currentChartData?.chart_project !==
        msgData?.project?.id &&
      notification.message_event !== MSG_EVENT.FILE_PROCESSING &&
      notification.message_event !== MSG_EVENT.DATA_ANONYMIZED
    )
      message = generateNotificationWithButton({
        message: NotificationMsgWithLinks,
        buttonText: msgData.project.name,
        buttonLink: notificationLink as string,
        navigate: navigate,
        customButtonStyles
      });
    else message = <>{NotificationMsgWithLinks as JSX.Element[]}</>;
  else message = <>{NotificationMsgWithLinks as JSX.Element[]}</>;
  if (notification.message_event === 'CHART_THEME_UPDATED')
    message = (
      <>
        {message}
        <FaIcon
          icon='cog'
          typographyProps={{ color: 'dark', variant: 'lg' }}
          style={{ pointerEvents: 'none', marginLeft: 5 }}
        />
      </>
    );

  return message;
};

export const getThemePayload = ({
  themeType,
  chartId,
  projectId,
  projectName
}: {
  themeType: string;
  chartId?: string;
  projectId: string;
  projectName: string;
}) => {
  const payload: NotificationData = {
    created_at: dayjs(new Date())
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
    id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
    is_read: false,
    is_silent: false,
    is_user_message: true,
    message_event: chartId
      ? MSG_EVENT.CHART_THEME_UPDATED
      : MSG_EVENT.PROJECT_THEME_UPDATED,
    message_text: `${themeType} ${
      chartId ? 'chart' : 'charts'
    } enabled successfully for ${
      chartId
        ? `this chart!  If you want to apply ${themeType} charts to the whole Project, please go back to the Project Board and go to`
        : 'the Project'
    }!`,
    updated_at: dayjs(new Date())
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
    message_type: MSG_TYPE.SUCCESS,
    message_data: JSON.stringify({
      chart_id: chartId,
      project: {
        id: projectId,
        name: projectName
      }
    }),
    is_important: false,
    recipient_obj: 0
  };

  // dispatch(setUserNotifications(payload));
  // setThemeSnackBarOpen(false);

  return payload;
};
